import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Router } from '@angular/router';
import {IUserDetails} from '@irb/app.model';
import {AppService} from '@irb/app.service';
import {IrbRebalanceEvents} from '@irb-common/events';
import {RebalanceService} from "@irb/irb-editors/rebalance/rebalance.service";
import { AnalyticsService } from '@irb/irb-common/analytics/analytics.service';

@Injectable({
  providedIn: 'root'
})
export class MainMenuService {
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': 'Sat, 01 Jan 2000 00:00:00 GMT'
    })
  };
  flattenedRoutes: any[] = [];

  constructor(private _http: HttpClient, public appService: AppService, public rs: RebalanceService, private router: Router,private ats: AnalyticsService) {}

  logOut() {
    this.appService.logOut();
  }

  getMenuData(userDetails: IUserDetails) {
    const config: any = [
      {
        id: 'home',
        label: 'Home',
        routerLink:  userDetails.isMMCAdmin?'':'home',
        disabled: userDetails.isMMCAdmin
      },
      {
        id: 'reports',
        label: 'Reports',
        items: [
          {
            id: 'cash-report',
            label: 'Cash Report',
            click: (() => {
              this.ats.trackPassiveEvent("lc_cash");
              this.router.navigate(['/reports/cash-report']);
            }).bind(this),
            disabled: userDetails.isMMCAdmin
          },
          {
            id: 'allocation-report',
            label: 'Allocation Report',
            click: (() => {
              this.ats.trackPassiveEvent("lc_allocation");
              this.router.navigate(['/reports/allocation-report']);
            }).bind(this),
            disabled: userDetails.isMMCAdmin
          }
        ]
      },
      {
        id: 'clients',
        label: 'Clients',
        items: [
          {
            id: 'portfolios',
            label: 'Portfolios',
            click: (() => {
              this.ats.trackPassiveEvent("lc_ports");
              this.router.navigate(['/clients/portfolios']);
            }).bind(this),
            disabled: userDetails.isMMCAdmin
          },
          {
            id: 'accounts',
            label: 'Accounts',
            click: (() => {
              this.ats.trackPassiveEvent("lc_accts");
              this.router.navigate(['/clients/accounts']);
            }).bind(this),
            disabled: userDetails.isMMCAdmin,
          },
          {
            id: 'unassigned-accounts',
            label: 'Unassigned Accounts',
            click: (() => {
              this.ats.trackPassiveEvent("lc_unaccts");
              this.router.navigate(['/clients/unassigned-accounts']);
            }).bind(this),
            disabled: userDetails.isMMCAdmin,
          }
        ]
      },
      {
        id: 'models',
        label: 'Models',
        items: this.getModelsSubMenuData(userDetails)
      },
      {
        id: 'rebalancing',
        label: 'Rebalancing',
        items: [
          {
            id: 'create-a-rebalance',
            label: 'Create a Rebalance',
            click: (() => {
              this.ats.trackPassiveEvent("lc_createrebal");
              this.ats.trackCreateRebalanceEvent();
              this.appService.fireEvent(IrbRebalanceEvents.SHOW_EDIT_CREATE_REBALANCE, {fullscreen: true, source: null, navigationSource: null});
            }).bind(this),
          disabled: userDetails.isMMCAdmin
          },
          {
            id: 'rebalance-results',
            label: 'Rebalance Results',
            routerLink: '/rebalancing/rebalance-results',
            disabled: userDetails.isMMCAdmin
          },
          {
            id: 'scheduled-rebalances',
            label: 'Scheduled Rebalances',
            click: (() => {
              this.ats.trackPassiveEvent("lc_schedrebal");
              this.router.navigate(['/rebalancing/scheduled-rebalances']);
            }).bind(this),
            disabled: userDetails.isMMCAdmin
          }
        ]
      },
      {
        id: 'order-blotter',
        label: 'Order Blotter',
        disabled: !userDetails.isTrader,
        routerLink: userDetails.isTrader ? '/orders/order-blotter' : ''
      }
    ];

    if (!userDetails.isMMCAdmin && !userDetails.isModelProvider && userDetails.isShowAutoTrading) {
      config[1].items.push(this.getAutoRebalanceReport());
    }
    if (userDetails.isMMCAdmin)
      config[1].items.push(this.getPmmReportReport());

    this.flattenedRoutes  = this.flattenRoutes(config);
    return config;
  }

  private getModelsSubMenuData(userDetails: IUserDetails) {
    let items = [];
    if(!userDetails.isMMCAdmin && !userDetails.isModelSecManager) {
      items = [
        {
          id: 'models-inv',
          label: 'Models',
          disabled: true
        },
        {
          id: 'securitymaster',
          label: 'Securities',
          disabled:  true
        }
      ];
    } else {
      let modelsItem;
      let modelMarketCenterItem;
      items = [];
      if(userDetails.isMMCAdmin) {
        modelMarketCenterItem =  {
          id: 'model-market-center',
          label: 'Model Market Center',
          click: (() => {
            this.ats.trackPassiveEvent("lc_mmc");
            this.router.navigate(['models/model-market-center']);
          }).bind(this)
        };
        items.push(modelMarketCenterItem);
      } else {
        modelsItem = {
          id: 'models-inv',
          label: 'Models',
          click: (() => {
            this.ats.trackPassiveEvent("lc_models");
            this.router.navigate(['/models/models']);
          }).bind(this)
        };
        modelMarketCenterItem =  {
          id: 'model-market-center',
          label: 'Model Market Center',
          disabled: (!userDetails.isModelSecManager || (userDetails.isModelSecManager && userDetails.isHideModelMarketCenter)),
          click: (() => {
            this.ats.trackPassiveEvent("lc_mmc");
            this.router.navigate(['models/model-market-center']);
          }).bind(this)
        };
        items.push(...[modelsItem,modelMarketCenterItem]);
      }
      items.push(...[
        {
          id: 'securitymaster',
          label: 'Securities',
          routerLink: '/models/security-master',
          disabled: userDetails.isMMCAdmin
        }
      ]);
    }
    return items;
  }

  getAutoRebalanceReport () {
    return {
      id: 'auto-rebalance-report',
      label: 'Automated Rebalance Report',
      click: (() => {
        this.ats.trackPassiveEvent("lc_autorebalancereport");
        this.router.navigate(['/reports/auto-rebalance-report']);
      }).bind(this)
    }
  }

  private flattenRoutes(menuLinks: any[]) {
    let menuRoutes: any[] = [];
    if (menuLinks) {
      menuLinks.forEach((menuLink: any) => {
        if (menuLink.routerLink) {
          menuRoutes.push(menuLink);
        }
        if (menuLink.items && menuLink.items.length > 0) {
          let childRoutes: any[] = this.flattenRoutes(menuLink.items);
          if (childRoutes && childRoutes.length > 0) {
            menuRoutes.push(...childRoutes);
          }
        }
      });
    }
    return menuRoutes;
  }

  getPmmReportReport () {
    return {
      id: 'pmm-report',
      label: 'PMM Report',
      click: (() => {
        this.router.navigate(['/reports/pmm-report']);
      }).bind(this)
    }
  }
}

export interface MenuConfig {
  isBetaEnabled: boolean;
  isTrader: boolean;
  isAdmin: boolean;
  isModelSecManager: boolean;
  isIrebalAccess: boolean;
  isNoWebAccess: boolean;
  isModelProvider: boolean;
  isMMCAdmin: boolean;
  isSundryaAuthorizedSignor: boolean;
  isHideModelMarketCenter: boolean;
}
